import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getSupabaseUrl } from '../lib/environments';
import { linksApi } from '../services/linksApi';
import type { FacebookFriend } from '../lib/facebookParser';

const FB_IMPORT_API = `${getSupabaseUrl()}/functions/v1/facebook-import-api`;

interface FriendRow {
  name: string;
  contact: string;
  invited: boolean;
  loading: boolean;
}

export function FacebookFriendsInvite() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();
  const friends: FacebookFriend[] = location.state?.friends || [];

  const [rows, setRows] = useState<FriendRow[]>(
    friends.map(f => ({ name: f.name, contact: '', invited: false, loading: false }))
  );

  const updateContact = (index: number, value: string) => {
    setRows(prev => prev.map((r, i) => i === index ? { ...r, contact: value } : r));
  };

  const handleInvite = async (index: number) => {
    const row = rows[index];
    const contact = row.contact.trim();
    if (!contact) return;

    setRows(prev => prev.map((r, i) => i === index ? { ...r, loading: true } : r));

    const isEmail = contact.includes('@');
    const result = await linksApi.connect(
      isEmail ? { email: contact } : { phone: contact }
    );

    if (result.success) {
      // Mark as invited in DB (fire and forget)
      fetch(`${FB_IMPORT_API}?action=mark-friend-invited`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: row.name,
          ...(isEmail ? { email: contact } : { phone: contact }),
        }),
      }).catch(() => {});
    }

    setRows(prev => prev.map((r, i) =>
      i === index ? { ...r, loading: false, invited: result.success } : r
    ));
  };

  if (friends.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg p-8 text-center">
          <p className="text-gray-500 mb-4">No friends found in the export.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="py-3 px-8 bg-[#1877F2] text-white rounded-xl font-semibold hover:bg-[#1565D8] transition"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const invitedCount = rows.filter(r => r.invited).length;

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center p-4 py-8">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-1">Invite Facebook Friends</h2>
        <p className="text-sm text-gray-500 mb-6">
          Enter a phone number or email for each friend you'd like to invite to Kizu.
          {invitedCount > 0 && (
            <span className="text-green-600 font-medium"> {invitedCount} invited</span>
          )}
        </p>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {rows.map((row, i) => (
            <FriendItem
              key={i}
              row={row}
              onContactChange={(v) => updateContact(i, v)}
              onInvite={() => handleInvite(i)}
            />
          ))}
        </div>

        <button
          onClick={() => navigate('/dashboard')}
          className="w-full py-3 mt-6 bg-[#1877F2] text-white rounded-xl font-semibold hover:bg-[#1565D8] transition"
        >
          Done
        </button>
      </div>
    </div>
  );
}

function FriendItem({ row, onContactChange, onInvite }: {
  row: FriendRow;
  onContactChange: (value: string) => void;
  onInvite: () => void;
}) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{row.name}</p>
        {!row.invited && (
          <input
            type="text"
            placeholder="Phone or email"
            value={row.contact}
            onChange={(e) => onContactChange(e.target.value)}
            className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#1877F2] focus:border-[#1877F2]"
          />
        )}
      </div>
      {row.invited ? (
        <span className="text-xs font-semibold text-green-600 whitespace-nowrap flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Invited
        </span>
      ) : (
        <button
          onClick={onInvite}
          disabled={!row.contact.trim() || row.loading}
          className="text-xs font-semibold text-[#1877F2] hover:text-[#1565D8] disabled:text-gray-300 disabled:cursor-not-allowed whitespace-nowrap py-1 px-3 border border-[#1877F2] disabled:border-gray-200 rounded-lg transition"
        >
          {row.loading ? '...' : 'Invite'}
        </button>
      )}
    </div>
  );
}
